import {
  getLeoUuid,
  LeoUUID,
  getObject,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { BoundedName, BoundedNameDTO } from "../types/boundedName";
import { KeycloakEmail, KeycloakEmailDTO } from "../types/keycloakEmail";
import { EmailContent, EmailContentDTO } from "./emailContent";
import { ScheduleType } from "./scheduleType";
/* eslint-disable import/export */
export abstract class UpdateEmailCycleRPC implements
  LeoRPC<UpdateEmailCycleRPC.Request, UpdateEmailCycleRPC.Response, UpdateEmailCycleRPC.Errors.Errors>
{
  abstract execute(
    request: UpdateEmailCycleRPC.Request
  ): Promise<LeoRPCResult<UpdateEmailCycleRPC.Response, UpdateEmailCycleRPC.Errors.Errors>>;
}

export namespace UpdateEmailCycleRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    scheduleId: string;
    cycleName: BoundedNameDTO;
    senderEmail: KeycloakEmailDTO;
    senderName: BoundedNameDTO;
    emailContent: EmailContentDTO;
    scheduleType: ScheduleType.ScheduleTypeDTO;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isUpdateEmailCycleRPCRequest: boolean = true;
  
    readonly scheduleId: LeoUUID;
    readonly cycleName: BoundedName;
    readonly senderEmail: KeycloakEmail;
    readonly senderName: BoundedName;
    readonly emailContent: EmailContent;
    readonly scheduleType: ScheduleType.ScheduleType;
  
  
    constructor(
    scheduleId: LeoUUID, 
    cycleName: BoundedName, 
    senderEmail: KeycloakEmail, 
    senderName: BoundedName, 
    emailContent: EmailContent, 
    scheduleType: ScheduleType.ScheduleType
    ) {
      this.scheduleId = scheduleId;
      this.cycleName = cycleName;
      this.senderEmail = senderEmail;
      this.senderName = senderName;
      this.emailContent = emailContent;
      this.scheduleType = scheduleType;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const scheduleId = getLeoUuid(dto, "scheduleId", InvalidRequestScheduleIdError);
      const cycleName = BoundedName.fromDTO(getObject(dto, "cycleName", InvalidRequestCycleNameError));
      const senderEmail = KeycloakEmail.fromDTO(getObject(dto, "senderEmail", InvalidRequestSenderEmailError));
      const senderName = BoundedName.fromDTO(getObject(dto, "senderName", InvalidRequestSenderNameError));
      const emailContent = EmailContent.fromDTO(getObject(dto, "emailContent", InvalidRequestEmailContentError));
      const scheduleType = ScheduleType.fromDTO(getObject(dto, "scheduleType", InvalidRequestScheduleTypeError));
      return new Request(
        scheduleId,
        cycleName,
        senderEmail,
        senderName,
        emailContent,
        scheduleType
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          scheduleId: this.scheduleId.toDTO(),
          cycleName: this.cycleName.toDTO(),
          senderEmail: this.senderEmail.toDTO(),
          senderName: this.senderName.toDTO(),
          emailContent: this.emailContent.toDTO(),
          scheduleType: this.scheduleType.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `UpdateEmailCycleRPC(scheduleId='${this.scheduleId}',cycleName='${this.cycleName}',senderEmail='${this.senderEmail}',senderName='${this.senderName}',emailContent='${this.emailContent}',scheduleType='${this.scheduleType}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {}

  export class Response implements LeoRPCResponse {
    static fromDTO(): Response {
      return new Response();
    }

    toDTO(): ResponseDTO {
      return {}
    }
  }

  Response.prototype.toString = function toString(): string {
    return `UpdateEmailCycleRPC.Response()`;
  };

  export enum RPCError {
    InvalidScheduleId = "INVALID_SCHEDULE_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    InvalidSchedule = "INVALID_SCHEDULE",
    InvalidSenderEmail = "INVALID_SENDER_EMAIL",
    DomainNotAllowed = "DOMAIN_NOT_ALLOWED",
    DuplicateCycleName = "DUPLICATE_CYCLE_NAME",
    ScheduleInProgress = "SCHEDULE_IN_PROGRESS"
  }

  export namespace Errors {
    
    export interface InvalidScheduleIdDTO {
      code: string;
    }
    
    export class InvalidScheduleId extends LeoRPCError<RPCError> {
      readonly isInvalidScheduleId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidScheduleId;
        super(code);
      }
    
    
      toDTO(): InvalidScheduleIdDTO {
        const dto: InvalidScheduleIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidScheduleId.prototype.toString = function toString(): string {
      return `UpdateEmailCycleRPC.INVALID_SCHEDULE_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `UpdateEmailCycleRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface InvalidScheduleDTO {
      code: string;
    }
    
    export class InvalidSchedule extends LeoRPCError<RPCError> {
      readonly isInvalidSchedule: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSchedule;
        super(code);
      }
    
    
      toDTO(): InvalidScheduleDTO {
        const dto: InvalidScheduleDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSchedule.prototype.toString = function toString(): string {
      return `UpdateEmailCycleRPC.INVALID_SCHEDULE()`;
    };
    
    
    export interface InvalidSenderEmailDTO {
      code: string;
    }
    
    export class InvalidSenderEmail extends LeoRPCError<RPCError> {
      readonly isInvalidSenderEmail: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSenderEmail;
        super(code);
      }
    
    
      toDTO(): InvalidSenderEmailDTO {
        const dto: InvalidSenderEmailDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSenderEmail.prototype.toString = function toString(): string {
      return `UpdateEmailCycleRPC.INVALID_SENDER_EMAIL()`;
    };
    
    
    export interface DomainNotAllowedDTO {
      code: string;
    }
    
    export class DomainNotAllowed extends LeoRPCError<RPCError> {
      readonly isDomainNotAllowed: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.DomainNotAllowed;
        super(code);
      }
    
    
      toDTO(): DomainNotAllowedDTO {
        const dto: DomainNotAllowedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    DomainNotAllowed.prototype.toString = function toString(): string {
      return `UpdateEmailCycleRPC.DOMAIN_NOT_ALLOWED()`;
    };
    
    
    export interface DuplicateCycleNameDTO {
      code: string;
    }
    
    export class DuplicateCycleName extends LeoRPCError<RPCError> {
      readonly isDuplicateCycleName: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.DuplicateCycleName;
        super(code);
      }
    
    
      toDTO(): DuplicateCycleNameDTO {
        const dto: DuplicateCycleNameDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    DuplicateCycleName.prototype.toString = function toString(): string {
      return `UpdateEmailCycleRPC.DUPLICATE_CYCLE_NAME()`;
    };
    
    
    export interface ScheduleInProgressDTO {
      code: string;
    }
    
    export class ScheduleInProgress extends LeoRPCError<RPCError> {
      readonly isScheduleInProgress: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ScheduleInProgress;
        super(code);
      }
    
    
      toDTO(): ScheduleInProgressDTO {
        const dto: ScheduleInProgressDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ScheduleInProgress.prototype.toString = function toString(): string {
      return `UpdateEmailCycleRPC.SCHEDULE_IN_PROGRESS()`;
    };
    

    export type Errors = InvalidScheduleId | ProjectAlreadyArchived | InvalidSchedule | InvalidSenderEmail | DomainNotAllowed | DuplicateCycleName | ScheduleInProgress;
    export type ErrorsDTO = InvalidScheduleIdDTO | ProjectAlreadyArchivedDTO | InvalidScheduleDTO | InvalidSenderEmailDTO | DomainNotAllowedDTO | DuplicateCycleNameDTO | ScheduleInProgressDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidScheduleId:
          return new InvalidScheduleId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.InvalidSchedule:
          return new InvalidSchedule();
        case RPCError.InvalidSenderEmail:
          return new InvalidSenderEmail();
        case RPCError.DomainNotAllowed:
          return new DomainNotAllowed();
        case RPCError.DuplicateCycleName:
          return new DuplicateCycleName();
        case RPCError.ScheduleInProgress:
          return new ScheduleInProgress();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestScheduleIdError extends UpdateEmailCycleRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestScheduleIdError";
  }
}

export class InvalidRequestCycleNameError extends UpdateEmailCycleRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestCycleNameError";
  }
}

export class InvalidRequestSenderEmailError extends UpdateEmailCycleRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSenderEmailError";
  }
}

export class InvalidRequestSenderNameError extends UpdateEmailCycleRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSenderNameError";
  }
}

export class InvalidRequestEmailContentError extends UpdateEmailCycleRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestEmailContentError";
  }
}

export class InvalidRequestScheduleTypeError extends UpdateEmailCycleRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestScheduleTypeError";
  }
}


export class InvalidResponseInvalidScheduleIdError extends UpdateEmailCycleRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidScheduleIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends UpdateEmailCycleRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseInvalidScheduleError extends UpdateEmailCycleRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidScheduleError";
  }
}

export class InvalidResponseInvalidSenderEmailError extends UpdateEmailCycleRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSenderEmailError";
  }
}

export class InvalidResponseDomainNotAllowedError extends UpdateEmailCycleRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseDomainNotAllowedError";
  }
}

export class InvalidResponseDuplicateCycleNameError extends UpdateEmailCycleRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseDuplicateCycleNameError";
  }
}

export class InvalidResponseScheduleInProgressError extends UpdateEmailCycleRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseScheduleInProgressError";
  }
}

